import {BaseElement, BaseElementAsync, OSkinableInit} from "back/commons/basis";
import {IApp, IAppCtx, JAppDef} from "back/core/appFrame";
import {IReg, IUiEnv, REG} from 'lib/commons/registry';
import {IView} from "lib/commons/views";
import {JSX} from "lib/commons/xml/dom";
import {FontSizeDeskFeat, ThemeAction, UiThemeDeskFeat} from "back/core/plugins/optionsPlg";
import {captureScenariProtocol} from "lib/core/scenariProtocol";
import {JShowAppDef} from "back/core/plugins/showPlg";
import {IO, IResolverPointer} from "lib/commons/io/io";
import {POPUP} from "back/commons/widgets/popups";
import {DOMSH} from "lib/commons/xml/domsh";

/**
 * App constituée d'une simple iframe dont l'URL n'est pas spécifiable en paramètre de l'app mais par le code.
 *
 * @see ShowApp
 */
export interface FrameApp extends BaseElement, IView {
	initialize(init: OFrameAppInit): this;
}

export interface OFrameAppInit extends OSkinableInit, IAppCtx<IUiEnv> {
	reg: IReg<IUiEnv>
	url: string
	anonymousAllowed: boolean;
	name?: string
}

export class FrameApp extends BaseElementAsync implements IApp<IUiEnv>, IFrameAppParentFrame {

	anonymousAllowed: boolean;

	reg: IReg<IUiEnv & IResolverPointer>;

	appDef: JAppDef;

	protected async _initialize(init: OFrameAppInit) {
		this.appDef = init.appDef;
		this.reg = this.findReg(init) as IReg<IUiEnv>;
		this.anonymousAllowed = init.anonymousAllowed;
		if (init.name) this.setAttribute("label", init.name);
		const iframe = <iframe id="ifrm" src={init.url}/> as HTMLIFrameElement;

		/** @deprecated laissé pour compat asc. Passer plutot par {@link IWindowFrameApp.initParentFrame} */
		if (UiThemeDeskFeat.isIn(desk)) (iframe as any).injectTheme = UiThemeDeskFeat.injectTheme;

		iframe.addEventListener("load", this.onLoadedPage);
		this._attach(this.localName, init, iframe);
	}


	/** Chargement d'une nouvelle page. */
	protected async onLoadedPage(this: HTMLIFrameElement) {
		const frameApp = DOMSH.findHost(this) as FrameApp;
		const win = this.contentWindow as IWindowFrameApp;
		let winLoc;
		try {
			// Une exception est lancé si bloqué par la sécurité
			winLoc = win.location.href;
		} catch (e) {}
		if (!winLoc || winLoc === "about:blank") {
			//chargement intial de l'iframe ou reset de la page.
			return;
		}

		//déclaration du contexte parent.
		if (win.initParentFrame) win.initParentFrame(frameApp);

		captureScenariProtocol(this, frameApp.reg);
	}

	buildInitFromAtts(init?: OFrameAppInit): any {
		init = super.buildInitFromAtts(init);
		init.url = this.getAttribute("url");
		return init;
	}

	getFrame(): HTMLIFrameElement {return this.shadowRoot.getElementById("ifrm") as HTMLIFrameElement}

	protected _randomId: string;
	protected _themeLstn: (theme: ThemeAction) => void;

	updateAppDef(def: JAppDef): boolean {
		return false;
	}

	onViewHidden(closed?: boolean): void {
		if (closed && this._themeLstn && UiThemeDeskFeat.isIn(desk)) {
			desk.onThemeChange.delete(this._themeLstn);
			this._themeLstn = null;
		}
	}

	injectTheme(): void {
		if (UiThemeDeskFeat.isIn(desk)) desk.injectThemeInSubFrame(this.getFrame());
		if (FontSizeDeskFeat.isIn(desk)) desk.injectFontSizeInSubFrame(this.getFrame());
	}

	async hasPerm(perm: string): Promise<boolean> {
		const libPath = await REG.findScPermLibPath(perm);
		if (this.reg.env.resolver && libPath)
			await import(this.reg.env.resolver.resolvePath(libPath));
		return this.reg.hasPerm(perm);
	}
}

REG.reg.registerSkin('c-frame-app', 1, /* language=CSS */ `
	:host {
		flex: 1;
		display: flex;
		min-height: 0;
		min-width: 0;
	}

	iframe {
		flex: 1;
		width: 100%;
		height: 100%;
		border: none;
		/*background: white;*/
	}
`);

customElements.define('c-frame-app', FrameApp);

/**
 * API implémentable par les pages affichées dans ce contexte FrameApp
 */
export interface IWindowFrameApp extends Window {

	/** Appelé sur le onLoad de la page web */
	initParentFrame?(parentFrame: IFrameAppParentFrame): void
}

/**
 * Contexte parent des pages de l frameApp, fournit à la page du dynGen
 * via IWindowFrameApp.initParentFrame(parentFrame: IFrameAppParentFrame)
 */
export interface IFrameAppParentFrame {

	/** Injecte le thème courant dans la page */
	injectTheme(): void

	/** Evalue une permission donnée sur le contexte reg courant */
	hasPerm(perm: string): Promise<boolean>

}


/**
 * App constituée d'une simple iframe dont l'URL est spécifiée
 * 	- en paramètre direct de l'app. Dans ce cas, l'URL **DOIT** passer la couche de contrôle {@link IO.isRedirectValid} pour des questions de sécurité ;
 *	- en exploitant une URL déclarée via son id ;
 */
export interface ShowApp extends FrameApp {
	initialize(init: OShowAppInit): this;
}

export interface OShowAppInit extends OFrameAppInit {
	appDef: JShowAppDef,
	/** Liste blanche des domaines autorisés */
	whiteDomains?: string[]
	/** URLs préenregistrée, référençables par ID. Syntaxe : id:url */
	aliasUrls?: Dict<string>
}

export class ShowApp extends FrameApp {

	appDef: JShowAppDef;

	view: FrameApp;

	protected _initialize(init: OShowAppInit): Promise<void> {
		if (init.appDef.show?.alias) {
			if (init.aliasUrls)
				init.url = init.aliasUrls[init.appDef.show.alias]
			if (!init.url) {
				const alias = init.appDef.show?.alias;
				POPUP.confirm(`La configuration de la vue est invalide : ${alias}`, init.reg.env.uiRoot, {cancelLbl: null});
				init.url = "about:blank";
			}
		} else {
			init.url = init.appDef.show?.url;
			if (!IO.isRedirectValid(init.url, init.whiteDomains || true)) {
				const url = init.url;
				POPUP.confirm(`La configuration de la vue est invalide : ${url}`, init.reg.env.uiRoot, {cancelLbl: null});
				init.url = "about:blank";
			}
		}
		init.skin = "c-frame-app";
		return super._initialize(init);
	}
}

customElements.define('show-app', ShowApp);